/*!
=========================================================
* Regiform IMS - Chart of Accounts React Query Hooks
=========================================================
* Custom hooks for Chart of Accounts feature
* Uses React Query for data fetching and mutations
=========================================================
*/

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { accountService } from "../services/accountService";
import { useBottomNotificationStore } from "../stores/bottomNotificationStore";

// Query keys for cache management
export const accountKeys = {
  all: ["accounts"],
  lists: () => [...accountKeys.all, "list"],
  list: (filters) => [...accountKeys.lists(), { filters }],
  trees: () => [...accountKeys.all, "tree"],
  tree: (filters) => [...accountKeys.trees(), { filters }],
  details: () => [...accountKeys.all, "detail"],
  detail: (id) => [...accountKeys.details(), id],
  types: () => [...accountKeys.all, "types"],
};

/**
 * Hook to get all accounts with optional filtering
 * @param {Object} params - Filter parameters
 * @returns {Object} React Query result
 */
export const useAccounts = (params = {}) => {
  const showNotification = useBottomNotificationStore((s) => s.showNotification);
  return useQuery({
    queryKey: accountKeys.list(params),
    queryFn: () => accountService.getAccounts(params),
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    onError: (error) => {
      showNotification(error.message || "Failed to fetch accounts", "error");
    },
  });
};

/**
 * Hook to get accounts in tree structure
 * @param {Object} params - Filter parameters
 * @returns {Object} React Query result
 */
export const useAccountsTree = (params = {}) => {
  const showNotification = useBottomNotificationStore((s) => s.showNotification);
  return useQuery({
    queryKey: accountKeys.tree(params),
    queryFn: () => accountService.getAccountsTree(params),
    refetchOnMount: "always",
    onError: (error) => {
      showNotification(error.message || "Failed to fetch account tree", "error");
    },
  });
};

/**
 * Hook to get single account by ID
 * @param {number} id - Account ID
 * @returns {Object} React Query result
 */
export const useAccount = (id) => {
  const showNotification = useBottomNotificationStore((s) => s.showNotification);
  return useQuery({
    queryKey: accountKeys.detail(id),
    queryFn: () => accountService.getAccountById(id),
    enabled: !!id,
    onError: (error) => {
      showNotification(error.message || "Failed to fetch account details", "error");
    },
  });
};

/**
 * Hook to get the direct child accounts of a parent.
 * Used by AccountSelect to determine whether a currently-selected account
 * that fell outside the active/leaf filter is a rollup (has children) as
 * opposed to simply inactive — see finding 5, PR #221 review.
 * @param {number} parentId - Parent account ID (query is disabled while falsy)
 * @returns {Object} React Query result
 */
export const useChildAccounts = (parentId) => {
  const showNotification = useBottomNotificationStore((s) => s.showNotification);
  return useQuery({
    queryKey: [...accountKeys.all, "children", parentId],
    queryFn: () => accountService.getChildAccounts(parentId),
    enabled: !!parentId,
    onError: (error) => {
      showNotification(error.message || "Failed to fetch child accounts", "error");
    },
  });
};

/**
 * Hook to create new account (mutation)
 * @returns {Object} Mutation object
 */
export const useCreateAccount = () => {
  const queryClient = useQueryClient();
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation({
    mutationFn: accountService.createAccount,
    onSuccess: (data) => {
      // Invalidate all account lists and trees
      queryClient.invalidateQueries(accountKeys.lists());
      queryClient.invalidateQueries(accountKeys.trees());
      showNotification(`Account created successfully: ${data.code} - ${data.name}`, "success");
    },
    onError: (error) => {
      showNotification(error.message || "Failed to create account", "error");
    },
  });
};

/**
 * Hook to update existing account (mutation)
 * @returns {Object} Mutation object
 */
export const useUpdateAccount = () => {
  const queryClient = useQueryClient();
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation({
    mutationFn: ({ id, data }) => accountService.updateAccount(id, data),
    onSuccess: (data) => {
      // Invalidate all account lists, trees, and the specific account detail
      queryClient.invalidateQueries(accountKeys.lists());
      queryClient.invalidateQueries(accountKeys.trees());
      queryClient.invalidateQueries(accountKeys.detail(data.id));
      showNotification("Account updated successfully", "success");
    },
    onError: (error) => {
      showNotification(error.message || "Failed to update account", "error");
    },
  });
};

/**
 * Hook to delete account (mutation)
 * @returns {Object} Mutation object
 */
export const useDeleteAccount = () => {
  const queryClient = useQueryClient();
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation({
    mutationFn: accountService.deleteAccount,
    onSuccess: () => {
      // Invalidate all account lists and trees
      queryClient.invalidateQueries(accountKeys.lists());
      queryClient.invalidateQueries(accountKeys.trees());
      showNotification("Account deleted successfully", "success");
    },
    onError: (error) => {
      showNotification(error.message || "Failed to delete account", "error");
    },
  });
};

/**
 * Hook to get account types
 * @returns {Object} React Query result
 */
export const useAccountTypes = () => {
  const showNotification = useBottomNotificationStore((s) => s.showNotification);
  return useQuery({
    queryKey: accountKeys.types(),
    queryFn: accountService.getAccountTypes,
    onError: (error) => {
      showNotification(error.message || "Failed to fetch account types", "error");
    },
  });
};

/**
 * Hook to get account categories
 * @param {Object} params - Filter parameters (e.g., { type: "ASSET" })
 * @returns {Object} React Query result
 */
export const useAccountCategories = (params = {}) => {
  const showNotification = useBottomNotificationStore((s) => s.showNotification);
  return useQuery({
    queryKey: [...accountKeys.all, "categories", params],
    queryFn: () => accountService.getAccountCategories(params),
    onError: (error) => {
      showNotification(error.message || "Failed to fetch account categories", "error");
    },
  });
};
